<script setup>
import { ref, computed, onMounted } from 'vue'
import draggable from 'vuedraggable'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { listaIdiomas } from '@/utils/listaIdiomas'

// Props
const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idListaSistemaCabecalho: 0,
      nomeLista: '',
      descricaoLista: '',
      prefixoItemLista: '',
      permitePersonalizacao: false,
      exclusivaSistema: false,
      isListaEstrutural: null,
      especificacaoTecnica: ''
    })
  }
})

// Emits
const emit = defineEmits([
  'itemAtualizado',
  'cancelar',
  'changesDetected',
  'show-notification'
])

// --- Lógica de Permissões ---
const authStore = useAuthStore()

const temPermissaaoCompleta = computed(() => authStore.pode('LISTAS_SISTEMA_COMPLETO'))
const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaaoCompleta.value
})

// --- Lógica do Cabeçalho (Master) ---
const {
  item,
  isLoading: isSavingCabecalho,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// --- Lógica de "Novo Cabeçalho" vs "Editando" ---
const isHeaderSaved = ref(isEditing.value)
const isSavingHeaderLocal = ref(false)
const isItensFrameBlocked = computed(() => !isHeaderSaved.value || isReadOnly.value)

// --- Lógica de Colapsos ---
const isDadosBasicosOpen = ref(true)
const isConfigEstruturalOpen = ref(true)
const isItensListaOpen = ref(true)
const isDicionarioOpen = ref(true)

// Guarda o valor original de IsListaEstrutural para validar edicao
const originalIsListaEstrutural = ref(null)

// Estado para o Modal de Auditoria
const mostrarAuditoria = ref(false)

// GUARDIÃO: Opções Padrão para os novos CampoSelects
const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const opcoesStatusItem = [
  { id: true, label: 'Ativo' },
  { id: false, label: 'Inativo' }
]

// --- Filtro de Idioma (CampoBuscaLista) ---
const idiomaFiltro = ref('pt-BR')
const idiomaFiltroCodigo = ref('LST_IDIOMAS1')
const isIdiomaFiltroReadOnly = computed(() => idiomaFiltro.value !== 'pt-BR')

function onIdiomaSelecionado (item) {
  idiomaFiltro.value = item.textoLista || 'pt-BR'
  idiomaFiltroCodigo.value = item.idListaSistemaItem || 'LST_IDIOMAS1'
}

// --- Modal de Variantes de Idioma ---
const modalIdiomasAberto = ref(false)
const codigoSelecionadoParaIdiomas = ref('')
const prefixoSelecionadoParaIdiomas = ref('')

const itensDoCodigo = computed(() => {
  if (!codigoSelecionadoParaIdiomas.value) return []
  return itensDaLista.value.filter(i =>
    i.idListaSistemaItem === codigoSelecionadoParaIdiomas.value
  )
})

const idiomasDisponiveisParaCodigo = computed(() => {
  const presentes = new Set(itensDoCodigo.value.map(i => i.idioma))
  return listaIdiomas.filter(li => !presentes.has(li.id))
})

const novoItemIdioma = ref({
  textoLista: '',
  descricaoItem: '',
  ativo: true,
  idioma: ''
})

function abrirModalIdiomas (itemLista) {
  codigoSelecionadoParaIdiomas.value = itemLista.idListaSistemaItem
  prefixoSelecionadoParaIdiomas.value = itemLista.prefixoItemLista || item.value.prefixoItemLista
  novoItemIdioma.value = { textoLista: '', descricaoItem: '', ativo: true, idioma: '' }
  modalIdiomasAberto.value = true
}

function fecharModalIdiomas () {
  modalIdiomasAberto.value = false
  codigoSelecionadoParaIdiomas.value = ''
  prefixoSelecionadoParaIdiomas.value = ''
}

async function salvarItemIdioma () {
  if (!novoItemIdioma.value.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }
  if (!novoItemIdioma.value.idioma) {
    emit('show-notification', { message: 'Selecione um idioma.', type: 'error' })
    return
  }

  const dto = {
    textoLista: novoItemIdioma.value.textoLista,
    descricaoItem: novoItemIdioma.value.descricaoItem,
    ativo: novoItemIdioma.value.ativo,
    isSystem: false,
    idioma: novoItemIdioma.value.idioma,
    idItem: codigoSelecionadoParaIdiomas.value
  }

  try {
    const response = await api.post(`/ListasSistema/itens/${prefixoSelecionadoParaIdiomas.value}`, dto)
    itensDaLista.value.push(response.data)
    novoItemIdioma.value = { textoLista: '', descricaoItem: '', ativo: true, idioma: '' }
    emit('show-notification', { message: `Item adicionado em "${novoItemIdioma.value.idioma}".`, type: 'success' })

    if (item.value.isListaEstrutural) {
      await gerarConstantesListasEstruturais()
    }
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao adicionar variante de idioma.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

async function salvarEdicaoItemIdioma (itemParaSalvar) {
  if (!itemParaSalvar.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }

  const dto = {
    textoLista: itemParaSalvar.textoLista,
    descricaoItem: itemParaSalvar.descricaoItem,
    ativo: itemParaSalvar.ativo,
    isSystem: itemParaSalvar.isSystem || false,
    idioma: itemParaSalvar.idioma
  }

  try {
    await api.put(`/ListasSistema/itens/${itemParaSalvar.idSequenciaTabela}`, dto)
    const idx = itensDaLista.value.findIndex(i => i.idSequenciaTabela === itemParaSalvar.idSequenciaTabela)
    if (idx !== -1) {
      itensDaLista.value[idx] = { ...itensDaLista.value[idx], ...dto }
    }
    emit('show-notification', { message: 'Item atualizado.', type: 'success' })

    if (item.value.isListaEstrutural) {
      await gerarConstantesListasEstruturais()
    }
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao atualizar item.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Inativação em Cascata ---
async function inativarCascata (itemLista) {
  const novoStatus = !itemLista.ativo
  const acao = novoStatus ? 'reativar' : 'inativar'

  try {
    await api.patch(`/ListasSistema/itens/${itemLista.idSequenciaTabela}/status`, { ativo: novoStatus })
    emit('show-notification', { message: `Item ${acao}do em todos os idiomas.`, type: 'success' })
    await buscarItens()

    if (item.value.isListaEstrutural) {
      await gerarConstantesListasEstruturais()
    }
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || `Erro ao ${acao} o item.`
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Lógica do Dicionário de Metadados ---
const dicionarioItens = ref([])
const isLoadingDicionario = ref(false)
const tabelasDisponiveis = ref([])
const camposDisponiveis = ref([])
const isSavingMetadado = ref(false)

const novoMetadado = ref({
  contextType: 'Tenant',
  tabela: '',
  campo: ''
})

const contextOptions = [
  { id: 'Master', label: 'Master (Sistema)' },
  { id: 'Tenant', label: 'Tenant (Cliente)' }
]

async function buscarMetadados () {
  if (!isEditing.value || !item.value.prefixoItemLista) return
  isLoadingDicionario.value = true
  try {
    const res = await api.get(`/DicionarioMetadadosListas/ByCodigo/${item.value.prefixoItemLista}`)
    dicionarioItens.value = res.data
  } catch (error) {
    console.error('Erro ao buscar metadados:', error)
  } finally {
    isLoadingDicionario.value = false
  }
}

async function buscarTabelas () {
  try {
    const res = await api.get(`/DicionarioMetadadosListas/GetTables/${novoMetadado.value.contextType}`)
    tabelasDisponiveis.value = res.data.map(t => ({ id: t, label: t }))
    novoMetadado.value.tabela = ''
    novoMetadado.value.campo = ''
    camposDisponiveis.value = []
  } catch (error) {
    console.error('Erro ao buscar tabelas:', error)
  }
}

async function buscarCampos () {
  if (!novoMetadado.value.tabela) return
  try {
    const res = await api.get(`/DicionarioMetadadosListas/GetColumns/${novoMetadado.value.contextType}/${novoMetadado.value.tabela}`)
    camposDisponiveis.value = res.data.map(c => ({ id: c, label: c }))
    novoMetadado.value.campo = ''
  } catch (error) {
    console.error('Erro ao buscar campos:', error)
  }
}

async function adicionarMetadado () {
  if (!novoMetadado.value.tabela || !novoMetadado.value.campo) {
    emit('show-notification', { message: 'Selecione a tabela e o campo.', type: 'error' })
    return
  }

  isSavingMetadado.value = true
  const dto = {
    tabelaNome: novoMetadado.value.tabela,
    campoNome: novoMetadado.value.campo,
    codigoLista: item.value.prefixoItemLista,
    tipoLista: novoMetadado.value.contextType === 'Master' ? 'Sistema' : 'Cliente'
  }

  try {
    await api.post('/DicionarioMetadadosListas', dto)
    emit('show-notification', { message: 'Mapeamento adicionado com sucesso.', type: 'success' })
    novoMetadado.value.campo = ''
    await buscarMetadados()
  } catch (error) {
    console.error('Erro ao salvar metadado:', error)
    emit('show-notification', { message: 'Erro ao salvar o mapeamento.', type: 'error' })
  } finally {
    isSavingMetadado.value = false
  }
}

async function excluirMetadado (id) {
  try {
    await api.delete(`/DicionarioMetadadosListas/${id}`)
    emit('show-notification', { message: 'Mapeamento removido.', type: 'success' })
    await buscarMetadados()
  } catch (error) {
    console.error('Erro ao excluir metadado:', error)
    emit('show-notification', { message: 'Erro ao excluir o mapeamento.', type: 'error' })
  }
}

// --- Lógica dos Itens (CRUD Embutido) ---
const itensDaLista = ref([])
const isSavingOrdenacao = ref(false)
const isLoadingItens = ref(false)
const erroItens = ref(null)
const itemEmEdicao = ref(null)
const isSavingItem = ref(false)

// --- Controle de Ordem (ASC / DESC) ---
const ordemInvertida = ref(false)
const itensFiltrados = computed(() => {
  return itensDaLista.value.filter(i => i.idioma === idiomaFiltro.value)
})
const itensOrdenados = computed(() => {
  if (ordemInvertida.value) {
    return [...itensFiltrados.value].reverse()
  }
  return itensFiltrados.value
})

const modeloVazioItem = () => ({
  idSequenciaTabela: null,
  idListaSistemaCabecalho: item.value.idListaSistemaCabecalho,
  idListaSistemaItem: '',
  textoLista: '',
  descricaoItem: '',
  ativo: true,
  isSystem: false,
  idioma: 'pt-BR'
})

// --- Carregamento de Dados ---
async function buscarItens () {
  if (!isEditing.value || !item.value.prefixoItemLista) {
    console.log('[DEBUG NAV] buscarItens ignorado: isEditing=', isEditing.value, 'prefixo=', item.value.prefixoItemLista)
    return
  }
  isLoadingItens.value = true
  erroItens.value = null
  try {
    const url = `/ListasSistema/itens/${item.value.prefixoItemLista}?includeInactive=true`
    console.log('[DEBUG NAV] GET', url)
    const response = await api.get(url)
    itensDaLista.value = response.data
    console.log('[DEBUG NAV] buscarItens OK,', response.data.length, 'itens')
  } catch (error) {
    console.error('[DEBUG NAV] Erro ao buscar itens:', error)
    erroItens.value = 'Falha ao carregar os itens da lista.'
  } finally {
    isLoadingItens.value = false
  }
}

// --- Funções de Ação (CRUD Itens) ---
function iniciarNovoItem () {
  if (isReadOnly.value) return
  itemEmEdicao.value = modeloVazioItem()
}

function iniciarEdicaoItem (itemOriginal) {
  if (isReadOnly.value) return
  itemEmEdicao.value = { ...itemOriginal }
}

function cancelarEdicaoItem () {
  itemEmEdicao.value = null
}

async function salvarSubItem (itemParaSalvar) {
  console.log('[DEBUG NAV] salvarSubItem iniciado', itemParaSalvar?.idSequenciaTabela ? 'edicao' : 'novo')
  if (isReadOnly.value) return
  if (!itemParaSalvar.textoLista) {
    emit('show-notification', { message: 'O campo "Texto do Item" é obrigatório.', type: 'error' })
    return
  }

  // Valida duplicidade: (IdListaClienteItem, Idioma) deve ser único
  if (!itemParaSalvar.idSequenciaTabela && itemParaSalvar.idListaSistemaItem) {
    const duplicado = itensDaLista.value.some(i =>
      i.idListaSistemaItem === itemParaSalvar.idListaSistemaItem &&
      i.idioma === itemParaSalvar.idioma &&
      i.idSequenciaTabela !== itemParaSalvar.idSequenciaTabela
    )
    if (duplicado) {
      emit('show-notification', { message: `Já existe um item com o código "${itemParaSalvar.idListaSistemaItem}" no idioma "${itemParaSalvar.idioma}".`, type: 'error' })
      isSavingItem.value = false
      return
    }
  }

  isSavingItem.value = true
  const identificador = item.value.prefixoItemLista

  const dto = {
    textoLista: itemParaSalvar.textoLista,
    descricaoItem: itemParaSalvar.descricaoItem,
    ativo: itemParaSalvar.ativo,
    isSystem: itemParaSalvar.isSystem,
    sequenciaLista: itemParaSalvar.sequenciaLista,
    idioma: itemParaSalvar.idioma
  }

  try {
    let response
    if (itemParaSalvar.idSequenciaTabela) {
      const url = `/ListasSistema/itens/${itemParaSalvar.idSequenciaTabela}`
      response = await api.put(url, dto)
      emit('show-notification', { message: 'Item atualizado com sucesso.', type: 'success' })
    } else {
      const url = `/ListasSistema/itens/${identificador}`
      response = await api.post(url, dto)
      emit('show-notification', { message: 'Item criado com sucesso.', type: 'success' })
    }

    console.log('[DEBUG NAV] salvarSubItem API OK, atualizando lista localmente')
    cancelarEdicaoItem()

    // Atualiza o array local sem buscar do servidor (evita re-render completo)
    if (itemParaSalvar.idSequenciaTabela) {
      // PUT: o backend retorna NoContent, entao mesclamos o dto no item existente
      const idx = itensDaLista.value.findIndex(i => i.idSequenciaTabela === itemParaSalvar.idSequenciaTabela)
      if (idx !== -1) {
        itensDaLista.value[idx] = {
          ...itensDaLista.value[idx],
          textoLista: dto.textoLista,
          descricaoItem: dto.descricaoItem,
          ativo: dto.ativo,
          isSystem: dto.isSystem,
          sequenciaLista: dto.sequenciaLista ?? itensDaLista.value[idx].sequenciaLista,
          idioma: dto.idioma
        }
      }
    } else {
      // POST: o backend retorna o item criado com idSequenciaTabela e idListaSistemaItem
      const itemSalvo = response.data
      itensDaLista.value.push(itemSalvo)
    }

    // Se a lista for estrutural, regenera constantes automaticamente
    if (item.value.isListaEstrutural) {
      console.log('[DEBUG NAV] Lista estrutural, gerando constantes...')
      await gerarConstantesListasEstruturais()
    }
    console.log('[DEBUG NAV] salvarSubItem COMPLETO - sem redirecionamento')
  } catch (error) {
    console.error('[DEBUG NAV] Erro ao salvar item:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o item.'
    emit('show-notification', { message: errorMsg, type: 'error' })
    // Em caso de erro, recarrega do servidor para garantir consistência
    await buscarItens()
  } finally {
    isSavingItem.value = false
  }
}

async function handleDragEnd () {
  if (isReadOnly.value) return

  // Atualiza as sequências localmente baseando-se na nova posição
  itensDaLista.value = itensDaLista.value.map((item, index) => ({
    ...item,
    sequenciaLista: index + 1
  }))

  isSavingOrdenacao.value = true
  try {
    // Monta o payload com IDs e as novas sequências
    const ordenacaoDto = itensDaLista.value.map(item => ({
      idSequenciaTabela: item.idSequenciaTabela,
      sequenciaLista: item.sequenciaLista
    }))

    // Atualizado para a nova rota de itens no backend
    await api.post(`/ListasSistema/itens/${item.value.prefixoItemLista}/reordenar`, ordenacaoDto)

    emit('show-notification', { message: 'Ordem atualizada com sucesso.', type: 'success' })

    // Se a lista for estrutural, regenera constantes automaticamente
    if (item.value.isListaEstrutural) {
      await gerarConstantesListasEstruturais()
    }
  } catch (error) {
    console.error('Erro ao salvar ordenação:', error)
    emit('show-notification', { message: 'Erro ao salvar a nova ordem.', type: 'error' })
    // Reverte a ordem em caso de erro (opcional, buscando do banco novamente)
    await buscarItens()
  } finally {
    isSavingOrdenacao.value = false
  }
}

// --- Função Auxiliar: Geração Automática de Constantes Estruturais ---

async function gerarConstantesListasEstruturais () {
  console.log('[DEBUG NAV] gerarConstantesListasEstruturais INICIADO')
  try {
    // allowInProduction=true permite que o endpoint funcione em produção
    await api.post('/Setup/gerar-classes-constantes?allowInProduction=true')
    console.log('[DEBUG NAV] Constantes geradas com sucesso.')
    emit('show-notification', { message: 'Classes de constantes regeneradas com sucesso.', type: 'success' })
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message
    const status = error.response?.status
    console.error('[DEBUG NAV] Erro ao gerar constantes (status=' + status + '):', errorMsg)
    if (status !== 403) {
      emit('show-notification', { message: `Erro ao regenerar constantes: ${errorMsg}`, type: 'error' })
    }
  }
}

// --- Função: Copiar Itens como CSV ---
function copiarItensCSV () {
  if (!itensDaLista.value.length) {
    emit('show-notification', { message: 'Não há itens para copiar.', type: 'error' })
    return
  }

  const cabecalho = ['#', 'Código', 'Texto do Item', 'Descrição Detalhada', 'Idioma', 'Sistema?', 'Status']
  const linhas = itensDaLista.value.map(item => [
    item.sequenciaLista ?? '',
    item.idListaSistemaItem ?? '',
    `"${(item.textoLista || '').replace(/"/g, '""')}"`,
    `"${(item.descricaoItem || '').replace(/"/g, '""')}"`,
    item.idioma ?? '',
    item.isSystem ? 'Sim' : 'Não',
    item.ativo ? 'Ativo' : 'Inativo'
  ])

  const csv = [cabecalho.join(';'), ...linhas.map(l => l.join(';'))].join('\n')

  navigator.clipboard.writeText(csv).then(() => {
    emit('show-notification', { message: `CSV copiado (${itensDaLista.value.length} itens).`, type: 'success' })
  }).catch(() => {
    emit('show-notification', { message: 'Erro ao copiar CSV.', type: 'error' })
  })
}

// --- Funções de Ação (Formulário Principal) ---

// Botão "Salvar Cabeçalho e Liberar Itens"
async function salvarCabecalhoLocalmente () {
  console.log('[DEBUG NAV] salvarCabecalhoLocalmente INICIADO')
  if (isReadOnly.value) return

  // Validação obrigatória de IsListaEstrutural
  if (item.value.isListaEstrutural === null || item.value.isListaEstrutural === undefined) {
    emit('show-notification', { message: 'Você deve informar se a lista é estrutural ou não.', type: 'error' })
    return
  }

  isSavingHeaderLocal.value = true

  const payload = {
    nomeLista: item.value.nomeLista,
    descricaoLista: item.value.descricaoLista,
    prefixoItemLista: item.value.prefixoItemLista ? item.value.prefixoItemLista.toUpperCase() : '',
    permitePersonalizacao: item.value.permitePersonalizacao,
    exclusivaSistema: item.value.exclusivaSistema,
    isListaEstrutural: item.value.isListaEstrutural,
    especificacaoTecnica: item.value.especificacaoTecnica
  }

  try {
    const apiUrl = '/ListasSistema'
    console.log('[DEBUG NAV] Fazendo POST para /ListasSistema')
    const response = await api.post(apiUrl, payload)
    console.log('[DEBUG NAV] POST /ListasSistema OK, response:', response.data)

    item.value = response.data

    isHeaderSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idListaSistemaCabecalho}`

    emit('show-notification', { message: 'Cabeçalho salvo. Agora pode adicionar itens.', type: 'success' })
    console.log('[DEBUG NAV] Buscando itens...')
    await buscarItens()

    // Se for estrutural, dispara geração automática de constantes
    if (item.value.isListaEstrutural) {
      console.log('[DEBUG NAV] Lista estrutural, gerando constantes...')
      await gerarConstantesListasEstruturais()
    }
    console.log('[DEBUG NAV] salvarCabecalhoLocalmente COMPLETO - sem redirecionamento')
  } catch (error) {
    console.error('[DEBUG NAV] Erro ao salvar cabeçalho localmente:', error)
    const data = error.response?.data
    let errorMsg = 'Erro ao salvar o cabeçalho.'
    if (typeof data === 'string') {
      errorMsg = data
    } else if (data?.message) {
      errorMsg = data.message
    } else if (data?.title) {
      errorMsg = data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingHeaderLocal.value = false
  }
}

// Botão "Salvar" principal (do topo)
async function submitFormDoCabecalho () {
  console.log('[DEBUG NAV] submitFormDoCabecalho FOI CHAMADO!')
  if (isReadOnly.value) return

  const payload = {
    nomeLista: item.value.nomeLista,
    descricaoLista: item.value.descricaoLista,
    prefixoItemLista: item.value.prefixoItemLista ? item.value.prefixoItemLista.toUpperCase() : '',
    permitePersonalizacao: item.value.permitePersonalizacao,
    exclusivaSistema: item.value.exclusivaSistema,
    isListaEstrutural: item.value.isListaEstrutural,
    especificacaoTecnica: item.value.especificacaoTecnica
  }

  await submitForm(
    'ListasSistema',
    'idListaSistemaCabecalho',
    payload
  )

  // Se for estrutural, dispara geração automática de constantes
  if (item.value.isListaEstrutural) {
    await gerarConstantesListasEstruturais()
  }
}

// Botão "Salvar Alterações" (sem fechar o formulário)
async function salvarAlteracoes () {
  console.log('[DEBUG NAV] salvarAlteracoes FOI CHAMADO!')
  if (isReadOnly.value) return

  // Validação obrigatória de IsListaEstrutural
  if (item.value.isListaEstrutural === null || item.value.isListaEstrutural === undefined) {
    emit('show-notification', { message: 'Você deve informar se a lista é estrutural ou não.', type: 'error' })
    return
  }

  isSavingCabecalho.value = true

  const payload = {
    nomeLista: item.value.nomeLista,
    descricaoLista: item.value.descricaoLista,
    prefixoItemLista: item.value.prefixoItemLista ? item.value.prefixoItemLista.toUpperCase() : '',
    permitePersonalizacao: item.value.permitePersonalizacao,
    exclusivaSistema: item.value.exclusivaSistema,
    isListaEstrutural: item.value.isListaEstrutural,
    especificacaoTecnica: item.value.especificacaoTecnica
  }

  try {
    const apiUrl = '/ListasSistema'
    let response

    if (isEditing.value) {
      const id = item.value.idListaSistemaCabecalho
      response = await api.put(`${apiUrl}/${id}`, payload)
    } else {
      response = await api.post(apiUrl, payload)
    }

    console.log('[DEBUG NAV] salvarAlteracoes API OK, response:', response.data)

    // Atualiza o item local sem emitir itemAtualizado (não fecha o painel)
    item.value = response.data
    isEditing.value = true
    isHeaderSaved.value = true
    entityInfo.value = `Editando ID: ${item.value.idListaSistemaCabecalho}`

    emit('show-notification', { message: 'Alterações salvas com sucesso.', type: 'success' })

    // Se for estrutural, dispara geração automática de constantes
    if (item.value.isListaEstrutural) {
      console.log('[DEBUG NAV] Lista estrutural, gerando constantes...')
      await gerarConstantesListasEstruturais()
    }
    console.log('[DEBUG NAV] salvarAlteracoes COMPLETO - sem fechar formulário')
  } catch (error) {
    console.error('[DEBUG NAV] Erro ao salvar alterações:', error)
    const data = error.response?.data
    let errorMsg = 'Erro ao salvar as alterações.'
    if (typeof data === 'string') {
      errorMsg = data
    } else if (data?.message) {
      errorMsg = data.message
    } else if (data?.title) {
      errorMsg = data.title
    }
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingCabecalho.value = false
  }
}

// --- Ciclo de Vida ---
onMounted(async () => {
  // GUARDIÃO: Listener para detectar navegação inesperada
  window.addEventListener('beforeunload', () => {
    console.log('[DEBUG NAV] beforeunload disparado! Razão desconhecida.')
  })

  if (isEditing.value) {
    buscarItens()
    buscarMetadados()
    // Guarda o valor original de IsListaEstrutural para bloquear reversao
    originalIsListaEstrutural.value = item.value.isListaEstrutural
  }
  buscarTabelas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Listas do Sistema"
    :is-loading="isSavingCabecalho"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormDoCabecalho"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
    :desabilitar-salvar="isReadOnly"
    :desabilitar-salvar-alteracoes="isReadOnly"
    :ocultar-salvar="!isHeaderSaved"
  >
    <!-- Slot de Ações do Header -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="mostrarAuditoria = true"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center cursor-pointer" @click="isDadosBasicosOpen = !isDadosBasicosOpen">
        <h5 class="mb-0">Dados Básicos</h5>
        <i class="bi" :class="isDadosBasicosOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="isDadosBasicosOpen" class="card-body">
        <div class="row g-3">
          <div class="col-md-5">
            <CampoInput
              id="nomeLista"
              label="Nome da Lista"
              v-model="item.nomeLista"
              placeholder='Ex: "Status Simples"'
              required
              :readonly="isReadOnly"
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="prefixoItemLista"
              label="Prefixo (Código)"
              v-model="item.prefixoItemLista"
              placeholder='Ex: "ST"'
              required
              maxlength="50"
              :readonly="isReadOnly || isEditing"
              :title="isEditing ? 'Não é permitido alterar o prefixo após a criação' : ''"
              @input="item.prefixoItemLista = item.prefixoItemLista.toUpperCase()"
            />
          </div>

          <!-- GUARDIÃO: Switch trocado por CampoSelect Sim/Não -->
          <div class="col-md-3">
            <CampoSelect
              id="permitePersonalizacao"
              label="Permite Personalização?"
              v-model="item.permitePersonalizacao"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              :disabled="isReadOnly"
              required
            />
          </div>

          <div class="col-md-3">
            <CampoSelect
              id="exclusivaSistema"
              label="Exclusiva do Sistema?"
              v-model="item.exclusivaSistema"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              :disabled="isReadOnly"
              required
            />
            <small class="text-muted">
              <i class="bi bi-info-circle me-1"></i>
              Listas exclusivas do sistema nunca são migradas para os clientes.
            </small>
          </div>

          <div class="col-12">
            <CampoInput
              id="descricaoLista"
              label="Descrição"
              type="textarea"
              v-model="item.descricaoLista"
              :readonly="isReadOnly"
            />
          </div>

        </div>
      </div>
    </div>

    <!-- Frame 1.5: Configuração Estrutural -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center cursor-pointer" @click="isConfigEstruturalOpen = !isConfigEstruturalOpen">
        <h5 class="mb-0">Configuração Estrutural</h5>
        <i class="bi" :class="isConfigEstruturalOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-show="isConfigEstruturalOpen" class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="d-flex align-items-center gap-1">
              <CampoSelect
                id="isListaEstrutural"
                label="É Lista Estrutural?"
                v-model="item.isListaEstrutural"
                :options="simNaoOptions"
                option-value="id"
                option-label="label"
                required
                :disabled="isReadOnly || (isEditing && originalIsListaEstrutural === true)"
              />
              <i
                class="bi bi-info-circle text-primary"
                style="cursor: help; font-size: 1.1rem; margin-top: -12px;"
                title="Lista Estrutural: significa que esta lista tera constantes registradas no codigo-fonte.
Os apps do cliente usarao estas constantes como referencia, nunca consultando o banco de dados diretamente.
ATENCAO: Uma vez salva como estrutural, esta configuracao nao pode ser revertida."
              ></i>
            </div>
          </div>
          <div class="col-12">
            <CampoInput
              id="especificacaoTecnica"
              label="Especificação Técnica"
              type="textarea"
              v-model="item.especificacaoTecnica"
              :readonly="isReadOnly"
              placeholder="Descreva detalhadamente a finalidade tecnica desta lista..."
              rows="4"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar Cabeçalho (apenas criação) -->
    <div class="mb-4" v-if="!isHeaderSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarCabecalhoLocalmente"
        :disabled="isSavingHeaderLocal || !item.nomeLista || !item.prefixoItemLista"
      >
        <span v-if="isSavingHeaderLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Cabeçalho e Liberar Itens
      </button>
    </div>

    <!-- Frame 2: Itens da Lista -->
    <fieldset :disabled="isItensFrameBlocked" class="mb-4">
      <div class="card" :class="{'frame-bloqueado': isItensFrameBlocked}">
        <div class="card-header d-flex justify-content-between align-items-center cursor-pointer" @click="isItensListaOpen = !isItensListaOpen">
          <div class="d-flex align-items-center flex-wrap gap-2">
            <h5 class="mb-0 me-3">Itens da Lista</h5>

            <!-- Filtro de Idioma -->
            <div v-show="isItensListaOpen && !isItensFrameBlocked" class="d-inline-flex align-items-center" @click.stop>
              <CampoBuscaLista
                id="filtroIdioma"
                label="Idioma"
                prefixo="LST_IDIOMAS"
                contexto="Sistema"
                :model-value="idiomaFiltroCodigo"
                hide-label
                style="max-width: 200px;"
                @selecionado="onIdiomaSelecionado"
              />
            </div>

            <button
              v-show="isItensListaOpen && !isItensFrameBlocked && !isIdiomaFiltroReadOnly"
              type="button"
              class="btn btn-primary btn-sm"
              @click.stop="iniciarNovoItem"
              :disabled="!!itemEmEdicao || isReadOnly"
            >
              <i class="bi bi-plus-lg me-1"></i> Adicionar Item
            </button>
            <button
              v-show="isItensListaOpen && !isItensFrameBlocked && itensDaLista.length > 0"
              type="button"
              class="btn btn-outline-secondary btn-sm ms-2"
              @click.stop="copiarItensCSV"
              title="Copiar itens como CSV (área de transferência)"
            >
              <i class="bi bi-clipboard me-1"></i> CSV
            </button>
            <div
              v-show="isItensListaOpen && !isItensFrameBlocked && itensDaLista.length > 0"
              class="d-inline-flex align-items-center ms-2"
              @click.stop
            >
              <div class="form-check form-switch mb-0 d-flex align-items-center p-0 m-0" style="min-height: 0;">
                <input
                  class="form-check-input cursor-pointer m-0"
                  type="checkbox"
                  id="chkOrdemInvertida"
                  v-model="ordemInvertida"
                  style="margin-left: 0;"
                />
                <label class="form-check-label text-muted small fw-medium cursor-pointer ms-1 mb-0" for="chkOrdemInvertida">
                  <i class="bi bi-arrow-down-up me-1"></i> DESC
                </label>
              </div>
            </div>
          </div>
          <i class="bi" :class="isItensListaOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>

        <div v-show="isItensListaOpen" class="card-body">

          <!-- Mensagens -->
          <div v-if="isItensFrameBlocked" class="alert alert-info border-0 shadow-sm text-center">
            <i class="bi bi-info-circle me-2"></i>
            Salve os Dados Básicos (acima) para adicionar ou editar itens.
          </div>

          <div v-else-if="isLoadingItens" class="text-center text-muted py-5">
            <span class="spinner-border spinner-border-sm me-2"></span>
            A carregar itens...
          </div>

          <div v-else-if="erroItens" class="alert alert-danger border-0 shadow-sm">{{ erroItens }}</div>

          <!-- Container da Tabela -->
          <div v-else class="admin-table-container">
            <table class="table admin-table">
              <thead>
                <tr>
                  <th style="width: 35px;" class="text-center">#</th>
                  <th style="width: 300px;">Código</th>
                  <th style="width: 400px;">Texto do Item</th>
                  <th style="width: 50%;">Descrição Detalhada</th>
                  <th style="width: 80px;" class="text-center">Idioma</th>
                  <th style="width: 80px;" class="text-center">Sistema?</th>
                  <th style="width: 80px;" class="text-center">Status</th>
                  <th class="text-end" style="width: 80px;">Ações</th>
                </tr>
              </thead>

              <!-- Modo ASC (padrão): draggable com alça de arrasto -->
              <draggable
                v-if="!ordemInvertida"
                v-model="itensDaLista"
                tag="tbody"
                item-key="idSequenciaTabela"
                handle=".drag-handle"
                :disabled="isReadOnly || !!itemEmEdicao || isIdiomaFiltroReadOnly"
                @end="handleDragEnd"
              >
                <template #item="{ element: itemLista }">
                  <tr
                    :key="itemLista.idSequenciaTabela"
                    v-show="itemLista.idioma === idiomaFiltro"
                    :class="{'linha-edicao': itemEmEdicao && itemEmEdicao.idSequenciaTabela === itemLista.idSequenciaTabela}"
                  >
                    <!-- Alça de Arrasto -->
                    <td class="text-center drag-handle-cell">
                      <div v-if="!isReadOnly && !itemEmEdicao" class="drag-handle cursor-move" title="Arraste para reordenar">
                        <i class="bi bi-grip-vertical fs-5"></i>
                      </div>
                      <span v-else class="text-muted small">{{ itemLista.sequenciaLista }}</span>
                    </td>

                    <!-- Linha de Edição -->
                    <template v-if="itemEmEdicao && itemEmEdicao.idSequenciaTabela === itemLista.idSequenciaTabela">
                      <td>
                        <CampoInput :model-value="itemLista.idListaSistemaItem" readonly disabled hide-label />
                      </td>

                      <td>
                        <CampoInput v-model="itemEmEdicao.textoLista" required hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                      </td>

                      <td>
                        <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                      </td>

                      <td>
                        <CampoSelect
                          :id="'itemIdioma' + itemLista.idSequenciaTabela"
                          v-model="itemEmEdicao.idioma"
                          :options="listaIdiomas"
                          option-value="id"
                          option-label="label"
                          hide-label
                        />
                      </td>

                      <td class="text-center">
                        <CampoSelect
                          :id="'itemSistema' + itemLista.idSequenciaTabela"
                          v-model="itemEmEdicao.isSystem"
                          :options="simNaoOptions"
                          option-value="id"
                          option-label="label"
                          hide-label
                        />
                      </td>

                      <td>
                        <CampoSelect
                          :id="'itemAtivo' + itemLista.idSequenciaTabela"
                          v-model="itemEmEdicao.ativo"
                          :options="opcoesStatusItem"
                          option-value="id"
                          option-label="label"
                          hide-label
                        />
                      </td>

                      <td class="acoes-cell">
                        <div class="acoes-wrapper justify-content-end">
                          <button
                            type="button"
                            class="btn btn-success btn-sm"
                            @click="salvarSubItem(itemEmEdicao)"
                            :disabled="isSavingItem"
                          >
                            <i class="bi bi-check-lg"></i>
                          </button>

                          <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            @click="cancelarEdicaoItem"
                            :disabled="isSavingItem"
                          >
                            <i class="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </td>
                    </template>

                    <!-- Linha de Visualização -->
                    <template v-else>
                      <td class="text-body-secondary">{{ itemLista.idListaSistemaItem }}</td>
                      <td class="fw-bold">{{ itemLista.textoLista }}</td>
                      <td class="text-muted small">{{ itemLista.descricaoItem || '-' }}</td>

                      <td class="text-center text-muted small">
                        {{ itemLista.idioma || '-' }}
                      </td>

                      <td class="text-center">
                        <i v-if="itemLista.isSystem || itemLista.IsSystem" class="bi bi-check-circle-fill text-primary" title="Item de Sistema"></i>
                        <span v-else class="text-muted small">-</span>
                      </td>

                      <td class="text-center">
                        <div class="form-check form-switch d-inline-flex align-items-center p-0 m-0" style="min-height: 0;">
                          <input
                            class="form-check-input cursor-pointer"
                            type="checkbox"
                            :id="'switchAtivo_' + itemLista.idSequenciaTabela"
                            :checked="itemLista.ativo"
                            :disabled="!!itemEmEdicao || isSavingItem || isReadOnly || isIdiomaFiltroReadOnly"
                            @change="inativarCascata(itemLista)"
                          />
                          <label class="form-check-label small cursor-pointer ms-1 mb-0" :for="'switchAtivo_' + itemLista.idSequenciaTabela">
                            {{ itemLista.ativo ? 'Ativo' : 'Inativo' }}
                          </label>
                        </div>
                      </td>

                      <td class="acoes-cell">
                        <div class="acoes-wrapper justify-content-end">
                          <button
                            type="button"
                            class="btn-icon text-primary"
                            @click="iniciarEdicaoItem(itemLista)"
                            :disabled="!!itemEmEdicao || isSavingItem || isReadOnly || isIdiomaFiltroReadOnly"
                            title="Editar Item"
                          >
                            <i class="bi bi-pencil-fill"></i>
                          </button>

                          <button
                            type="button"
                            class="btn-icon text-info"
                            @click="abrirModalIdiomas(itemLista)"
                            :disabled="!!itemEmEdicao || isSavingItem"
                            title="Variantes de idioma"
                          >
                            <i class="bi bi-translate"></i>
                          </button>
                        </div>
                      </td>
                    </template>
                  </tr>
                </template>

                <template #footer>
                  <tr v-if="itemEmEdicao && !itemEmEdicao.idSequenciaTabela" class="linha-edicao">
                    <td class="text-center">
                      <i class="bi bi-plus-lg text-primary"></i>
                    </td>

                    <td>
                      <CampoInput :model-value="item.prefixoItemLista + '...'" readonly disabled hide-label />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.textoLista" required hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                    </td>

                    <td>
                      <CampoSelect
                        id="novoItemIdioma"
                        v-model="itemEmEdicao.idioma"
                        :options="listaIdiomas"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td class="text-center">
                      <CampoSelect
                        id="novoItemSistema"
                        v-model="itemEmEdicao.isSystem"
                        :options="simNaoOptions"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td>
                      <CampoSelect
                        id="novoItemAtivo"
                        v-model="itemEmEdicao.ativo"
                        :options="opcoesStatusItem"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td class="acoes-cell">
                      <div class="acoes-wrapper justify-content-end">
                        <button
                          type="button"
                          class="btn btn-success btn-sm"
                          @click="salvarSubItem(itemEmEdicao)"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-check-lg"></i>
                        </button>

                        <button
                          type="button"
                          class="btn btn-secondary btn-sm"
                          @click="cancelarEdicaoItem"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </draggable>

              <!-- Modo DESC (invertido): tbody estático sem drag, itens invertidos -->
              <tbody v-else>
                <!-- NOVO ITEM no topo (ordem invertida) -->
                <tr v-if="itemEmEdicao && !itemEmEdicao.idSequenciaTabela" class="linha-edicao">
                  <td class="text-center">
                    <i class="bi bi-plus-lg text-primary"></i>
                  </td>

                  <td>
                    <CampoInput :model-value="item.prefixoItemLista + '...'" readonly disabled hide-label />
                  </td>

                  <td>
                    <CampoInput v-model="itemEmEdicao.textoLista" required hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                  </td>

                  <td>
                    <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                  </td>

                  <td>
                    <CampoSelect
                      id="novoItemIdioma"
                      v-model="itemEmEdicao.idioma"
                      :options="listaIdiomas"
                      option-value="id"
                      option-label="label"
                      hide-label
                    />
                  </td>

                  <td class="text-center">
                    <CampoSelect
                      id="novoItemSistema"
                      v-model="itemEmEdicao.isSystem"
                      :options="simNaoOptions"
                      option-value="id"
                      option-label="label"
                      hide-label
                    />
                  </td>

                  <td>
                    <CampoSelect
                      id="novoItemAtivo"
                      v-model="itemEmEdicao.ativo"
                      :options="opcoesStatusItem"
                      option-value="id"
                      option-label="label"
                      hide-label
                    />
                  </td>

                  <td class="acoes-cell">
                    <div class="acoes-wrapper justify-content-end">
                      <button
                        type="button"
                        class="btn btn-success btn-sm"
                        @click="salvarSubItem(itemEmEdicao)"
                        :disabled="isSavingItem"
                      >
                        <i class="bi bi-check-lg"></i>
                      </button>

                      <button
                        type="button"
                        class="btn btn-secondary btn-sm"
                        @click="cancelarEdicaoItem"
                        :disabled="isSavingItem"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr
                  v-for="itemLista in itensOrdenados"
                  :key="itemLista.idSequenciaTabela"
                  v-show="itemLista.idioma === idiomaFiltro"
                  :class="{'linha-edicao': itemEmEdicao && itemEmEdicao.idSequenciaTabela === itemLista.idSequenciaTabela}"
                >
                  <!-- Número sequencial (sem alça de arrasto) -->
                  <td class="text-center drag-handle-cell">
                    <span class="text-muted small">{{ itemLista.sequenciaLista }}</span>
                  </td>

                  <!-- Linha de Edição -->
                  <template v-if="itemEmEdicao && itemEmEdicao.idSequenciaTabela === itemLista.idSequenciaTabela">
                    <td>
                      <CampoInput :model-value="itemLista.idListaSistemaItem" readonly disabled hide-label />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.textoLista" required hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                    </td>

                    <td>
                      <CampoInput v-model="itemEmEdicao.descricaoItem" hide-label @keydown.enter.prevent="salvarSubItem(itemEmEdicao)" />
                    </td>

                    <td>
                      <CampoSelect
                        :id="'itemIdioma' + itemLista.idSequenciaTabela"
                        v-model="itemEmEdicao.idioma"
                        :options="listaIdiomas"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td class="text-center">
                      <CampoSelect
                        :id="'itemSistema' + itemLista.idSequenciaTabela"
                        v-model="itemEmEdicao.isSystem"
                        :options="simNaoOptions"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td>
                      <CampoSelect
                        :id="'itemAtivo' + itemLista.idSequenciaTabela"
                        v-model="itemEmEdicao.ativo"
                        :options="opcoesStatusItem"
                        option-value="id"
                        option-label="label"
                        hide-label
                      />
                    </td>

                    <td class="acoes-cell">
                      <div class="acoes-wrapper justify-content-end">
                        <button
                          type="button"
                          class="btn btn-success btn-sm"
                          @click="salvarSubItem(itemEmEdicao)"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-check-lg"></i>
                        </button>

                        <button
                          type="button"
                          class="btn btn-secondary btn-sm"
                          @click="cancelarEdicaoItem"
                          :disabled="isSavingItem"
                        >
                          <i class="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </td>
                  </template>

                  <!-- Linha de Visualização -->
                  <template v-else>
                    <td class="text-body-secondary">{{ itemLista.idListaSistemaItem }}</td>
                    <td class="fw-bold">{{ itemLista.textoLista }}</td>
                    <td class="text-muted small">{{ itemLista.descricaoItem || '-' }}</td>

                    <td class="text-center text-muted small">
                      {{ itemLista.idioma || '-' }}
                    </td>

                    <td class="text-center">
                      <i v-if="itemLista.isSystem || itemLista.IsSystem" class="bi bi-check-circle-fill text-primary" title="Item de Sistema"></i>
                      <span v-else class="text-muted small">-</span>
                    </td>

                    <td class="text-center">
                      <div class="form-check form-switch d-inline-flex align-items-center p-0 m-0" style="min-height: 0;">
                        <input
                          class="form-check-input cursor-pointer"
                          type="checkbox"
                          :id="'switchAtivo_' + itemLista.idSequenciaTabela"
                          :checked="itemLista.ativo"
                          :disabled="!!itemEmEdicao || isSavingItem || isReadOnly || isIdiomaFiltroReadOnly"
                          @change="inativarCascata(itemLista)"
                        />
                        <label class="form-check-label small cursor-pointer ms-1 mb-0" :for="'switchAtivo_' + itemLista.idSequenciaTabela">
                          {{ itemLista.ativo ? 'Ativo' : 'Inativo' }}
                        </label>
                      </div>
                    </td>

                    <td class="acoes-cell">
                      <div class="acoes-wrapper justify-content-end">
                        <button
                          type="button"
                          class="btn-icon text-primary"
                          @click="iniciarEdicaoItem(itemLista)"
                          :disabled="!!itemEmEdicao || isSavingItem || isReadOnly || isIdiomaFiltroReadOnly"
                          title="Editar Item"
                        >
                          <i class="bi bi-pencil-fill"></i>
                        </button>

                        <button
                          type="button"
                          class="btn-icon text-info"
                          @click="abrirModalIdiomas(itemLista)"
                          :disabled="!!itemEmEdicao || isSavingItem"
                          title="Variantes de idioma"
                        >
                          <i class="bi bi-translate"></i>
                        </button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>

        </div> <!-- Fim card-body -->
      </div> <!-- Fim Frame 2 -->
    </fieldset>

    <!-- Frame 3: Dicionário de Metadados -->
    <fieldset :disabled="isItensFrameBlocked">
      <div class="card" :class="{'frame-bloqueado': isItensFrameBlocked}">
        <div class="card-header d-flex justify-content-between align-items-center cursor-pointer" @click="isDicionarioOpen = !isDicionarioOpen">
          <h5 class="mb-0">Dicionário Metadados</h5>
          <i class="bi" :class="isDicionarioOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>

        <div v-show="isDicionarioOpen" class="card-body">
          <div v-if="isItensFrameBlocked" class="alert alert-info border-0 shadow-sm text-center">
            <i class="bi bi-info-circle me-2"></i>
            Salve os Dados Básicos para mapear campos do banco de dados.
          </div>

          <template v-else>
            <div class="row g-3 align-items-end mb-4">
              <div class="col-md-3">
                <CampoSelect
                  id="contextType"
                  label="Contexto"
                  v-model="novoMetadado.contextType"
                  :options="contextOptions"
                  option-value="id"
                  option-label="label"
                  @update:model-value="buscarTabelas"
                />
              </div>
              <div class="col-md-4">
                <CampoSelect
                  id="tabelaMetadado"
                  label="Tabela"
                  v-model="novoMetadado.tabela"
                  :options="tabelasDisponiveis"
                  option-value="id"
                  option-label="label"
                  @update:model-value="buscarCampos"
                  :disabled="!tabelasDisponiveis.length"
                />
              </div>
              <div class="col-md-3">
                <CampoSelect
                  id="campoMetadado"
                  label="Campo"
                  v-model="novoMetadado.campo"
                  :options="camposDisponiveis"
                  option-value="id"
                  option-label="label"
                  :disabled="!camposDisponiveis.length"
                />
              </div>
              <div class="col-md-2">
                <button
                  type="button"
                  class="btn btn-primary w-100"
                  @click="adicionarMetadado"
                  :disabled="!novoMetadado.campo || isSavingMetadado"
                >
                  <span v-if="isSavingMetadado" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-plus-lg me-1"></i>
                  Vincular
                </button>
              </div>
            </div>

            <div v-if="isLoadingDicionario" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <span class="ms-2">Carregando mapeamentos...</span>
            </div>

            <div v-else-if="dicionarioItens.length === 0" class="text-center text-muted py-4 border rounded dicionario-empty-state">
              Nenhum mapeamento encontrado para esta lista.
            </div>

            <div v-else class="table-responsive border rounded dicionario-table-wrapper">
              <table class="table table-hover mb-0 dicionario-table">
                <thead>
                  <tr>
                    <th>Tabela</th>
                    <th>Campo</th>
                    <th>Tipo</th>
                    <th class="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="meta in dicionarioItens" :key="meta.idDicionarioMetadadosListas">
                    <td>{{ meta.tabelaNome }}</td>
                    <td><code>{{ meta.campoNome }}</code></td>
                    <td>
                      <span class="badge" :class="meta.tipoLista === 'Sistema' ? 'bg-info' : 'bg-primary'">
                        {{ meta.tipoLista }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-link text-danger p-0"
                        @click="excluirMetadado(meta.idDicionarioMetadadosListas)"
                        title="Remover Mapeamento"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </fieldset>

  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ListaSistemaCabecalho"
    :chave-primaria="item.idListaSistemaCabecalho"
    :titulo-registro="item.nomeLista"
    :tabelas-filhas="['ListaSistemaItem']"
    :chave-estrangeira="'idListaSistemaCabecalho'"
    contexto="master"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Modal de Variantes de Idioma -->
  <ModalFormulario
    v-if="modalIdiomasAberto"
    v-model="modalIdiomasAberto"
    :titulo="'Variantes de idioma — ' + codigoSelecionadoParaIdiomas"
    tamanho="xl"
    :ocultar-confirmar="true"
    texto-confirmar="Fechar"
    @update:model-value="fecharModalIdiomas"
  >
    <div class="mb-3">
      <p class="text-muted small mb-2">
        <i class="bi bi-info-circle me-1"></i>
        Gerencie as traduções do item <strong>{{ codigoSelecionadoParaIdiomas }}</strong>.
        Adicione novos idiomas ou edite os existentes. A exclusão não é permitida.
      </p>

      <!-- Grid de idiomas existentes -->
      <div class="table-responsive" style="max-height: 250px; overflow-y: auto;">
        <table class="table table-sm table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 100px;">Idioma</th>
              <th>Texto do Item</th>
              <th>Descrição</th>
              <th style="width: 80px;" class="text-center">Status</th>
              <th class="text-end" style="width: 60px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="itemIdioma in itensDoCodigo" :key="itemIdioma.idSequenciaTabela">
              <td>
                <span class="badge bg-info-subtle text-info-emphasis">{{ itemIdioma.idioma }}</span>
              </td>
              <td>
                <CampoInput
                  :id="'modalTexto_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.textoLista"
                  hide-label
                  size="sm"
                />
              </td>
              <td>
                <CampoInput
                  :id="'modalDesc_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.descricaoItem"
                  hide-label
                  size="sm"
                />
              </td>
              <td class="text-center">
                <CampoSelect
                  :id="'modalAtivo_' + itemIdioma.idSequenciaTabela"
                  v-model="itemIdioma.ativo"
                  :options="opcoesStatusItem"
                  option-value="id"
                  option-label="label"
                  hide-label
                />
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="salvarEdicaoItemIdioma(itemIdioma)"
                  title="Salvar alterações"
                >
                  <i class="bi bi-check-lg"></i>
                </button>
              </td>
            </tr>
            <tr v-if="itensDoCodigo.length === 0">
              <td colspan="5" class="text-center text-muted py-3">
                Nenhuma variante de idioma encontrada para este código.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr class="my-3" />

      <!-- Adicionar novo idioma -->
      <div class="row g-2 align-items-center">
        <div class="col-md-3">
          <CampoSelect
            id="novoIdiomaModal"
            label="Novo Idioma"
            v-model="novoItemIdioma.idioma"
            :options="idiomasDisponiveisParaCodigo"
            option-value="id"
            option-label="label"
          />
        </div>
        <div class="col-md-3">
          <CampoInput
            id="novoTextoModal"
            label="Texto do Item"
            v-model="novoItemIdioma.textoLista"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="novoDescModal"
            label="Descrição"
            v-model="novoItemIdioma.descricaoItem"
          />
        </div>
        <div class="col-md-2">
          <button
            type="button"
            class="btn btn-primary w-100"
            @click="salvarItemIdioma"
            :disabled="!novoItemIdioma.idioma || !novoItemIdioma.textoLista"
          >
            <i class="bi bi-plus-lg me-1"></i> Adicionar
          </button>
        </div>
      </div>
    </div>
  </ModalFormulario>
</template>

<style scoped>
/* Estilos mantidos originais e adaptados para Dark Mode */
.acoes-cell {
  padding-right: 0.75rem;
}

.acoes-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
}

.frame-bloqueado {
  opacity: 0.7;
  background-color: var(--bs-secondary-bg);
  pointer-events: none;
  border-radius: 0.375rem;
}

/* --- Estilos da Tabela Embutida --- */
.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.375rem;
  height: 327px;
  min-height: 150px;
  resize: vertical;
  overflow-y: auto;
  overflow-x: auto;
}

.table.admin-table {
  margin-bottom: 0;
  border-collapse: collapse;
  table-layout: auto;
  color: var(--bs-body-color);
}

.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  border-bottom-width: 2px;
  border-bottom-color: var(--bs-border-color-translucent);
  vertical-align: middle;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  height: 45px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.admin-table tbody {
  min-height: 280px;
}

.admin-table td, .admin-table th {
  padding: 0.25rem 0.75rem;
  height: 40px;
  vertical-align: middle;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  border-right: 1px solid var(--bs-border-color-translucent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease-out;
}

.admin-table th:nth-child(2),
.admin-table td:nth-child(2),
.admin-table th:nth-child(3),
.admin-table td:nth-child(3) {
  white-space: normal;
  width: auto;
}

.admin-table th:last-child,
.admin-table td:last-child {
  border-right: none;
}

/* Fundo diferenciado para a linha em edição */
.admin-table tr.linha-edicao td {
  background-color: var(--bs-tertiary-bg) !important;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
}

:deep(td .form-label) {
  display: none;
}

.admin-table tbody tr:not(.linha-edicao):hover td {
  background-color: var(--bs-secondary-bg);
}

.acoes-cell {
  text-align: right;
  position: relative;
}

.acoes-wrapper {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease-out;
}

tr:not(.linha-edicao):hover .acoes-wrapper,
tr.linha-edicao .acoes-wrapper {
  opacity: 1;
}

.btn-icon {
  background: none;
  border: none;
  color: #6c7d8e;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}

.btn-icon:hover {
  background-color: #e2e6ea;
  color: #212934;
}

[data-bs-theme="dark"] .btn-icon:hover {
  background-color: #343a40;
  color: #fff;
}

.btn-icon i {
  font-size: 0.9rem;
  vertical-align: middle;
}

.cursor-pointer {
  cursor: pointer;
}

.card-header h5 {
  user-select: none;
}

.card-header i.bi-chevron-up,
.card-header i.bi-chevron-down {
  transition: transform 0.2s;
}

.drag-handle-cell {
  background-color: var(--bs-tertiary-bg);
  width: 40px;
}

.drag-handle {
  color: var(--bs-secondary-color);
  opacity: 0.5;
  transition: opacity 0.2s;
}

.drag-handle:hover {
  opacity: 1;
}

.cursor-move {
  cursor: move;
}

/* Garante que o item sendo arrastado não fique invisível */
.sortable-ghost {
  opacity: 0.4;
  background-color: var(--bs-primary-bg-subtle) !important;
}

.sortable-drag {
  opacity: 0.9;
  background-color: var(--bs-body-bg) !important;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

/* --- Dicionário Metadados: Tema Dark/Normal --- */

.dicionario-empty-state {
  background-color: var(--bs-tertiary-bg);
}

.dicionario-table-wrapper {
  border-color: var(--bs-border-color-translucent);
}

.dicionario-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  border-bottom-width: 2px;
  border-bottom-color: var(--bs-border-color-translucent);
  font-weight: 600;
  vertical-align: middle;
  padding: 0.5rem 0.75rem;
}

.dicionario-table tbody td {
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.dicionario-table tbody tr:hover td {
  background-color: var(--bs-secondary-bg);
}
</style>
